import axios, { AxiosResponse } from 'axios';
import { LoginResponse, VedomListRequest, RouteRequest } from '../types/api.types';
import { tokenManager } from '../helpers/token_manager';

const BASE_URL = process.env.REACT_APP_API_URL;

const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

let refreshTokenFunction: (() => Promise<void>) | null = null;

export const setRefreshTokenFunction = (fn: () => Promise<void>) => {
  refreshTokenFunction = fn;
};

api.interceptors.request.use(
  async (config) => {
    const expiresAt = localStorage.getItem('tokenExpiresAt');
    if (expiresAt && refreshTokenFunction) {
      await tokenManager.refreshIfNeeded(expiresAt, refreshTokenFunction);
    }

    const currentToken = localStorage.getItem('auth_token');
    if (currentToken) {
      config.headers.Authorization = `Bearer ${currentToken}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error) => {
    const status = error.response?.status;

    if ((status === 401 || status === 403) && window.location.pathname !== '/login') {
      console.warn('Unauthorized access, redirecting to login');
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      localStorage.removeItem('tokenExpiresAt');
      localStorage.removeItem('auth_login');
      localStorage.removeItem('auth_password');
      window.location.href = '/login';
      return Promise.reject(error);
    }

    if (status >= 400 && status < 500) {
      console.warn(`Client error ${status}:`, error.response?.data?.message || error.message);
      return Promise.reject(error);
    }

    if (status >= 500) {
      console.error(`Server error ${status}:`, error.response?.data?.message || error.message);
      return Promise.reject(error);
    }

    if (!error.response) {
      console.error('Network error:', error.message);
      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

export const authApi = {
  login: (data: { login: string; password: string }): Promise<LoginResponse> =>
    api.post('/blablacar/login', data).then((res) => res.data),
};

export const dataApi = {
  getVedomList: (data: VedomListRequest) =>
    api.post('/blablacar/vedomlist', data).then((res) => res.data),
  getRoute: (data: RouteRequest) =>
    api.post('/blablacar/route', data).then((res) => res.data),
  getVedomData: (id: string, date?: string) =>
    api.post('/blablacar/vedomdata', { id, date }).then((res) => res.data),
};

export const agentApi = {
  getAll: () => api.get('/agents').then((res) => res.data),
  create: (data: { name: string; color: string }) =>
    api.post('/agents', data).then((res) => res.data),
  update: (id: number, data: { name?: string; color?: string }) =>
    api.patch(`/agents/${id}`, data).then((res) => res.data),
  delete: (id: number) => api.delete(`/agents/${id}`),
};

export default api;